/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
import './style.scss';

import { useMemo, useRef, useState, useEffect } from 'react';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import ReactRouterPrompt from 'react-router-prompt';

import AddProjectProposalDialog from 'features/Council/components/AddProjectProposalDialog';
import {
  editForMultipleProjectProposals as editCouncil,
  getAllCouncilMemberRoles,
  getAllCouncilTypes,
  getCouncilMultipleProjectsById as getCouncil,
  downloadFile,
} from 'apis/councils.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { generalInfoForCouncil, getAllVersions } from 'apis/projectProposals.api';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import { DatePicker, FileInput, Select, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import MemberDialog from 'features/Council/components/AddMemberDialog';
import ConfirmSendMailMultipleProjectDialog from 'features/Council/components/ConfirmSendMailMultipleProjectDialog';
import { updateCouncilValidationSchema } from 'features/Council/validation';
import { toast } from 'layout';
import {
  getAcadamicLevelShortHand,
  getContactOrganizationName,
  getEndingTimeOfDate,
  getNameRoleOfMemberInCouncil,
  getOrganizationNameById,
  formatFileName,
} from 'utils/func';
import ConfirmDeleteMemberMulDialog from 'features/Council/components/ConfirmDeleteMemberMulDialog';
import DateTimePicker from 'components/FormControl/DateTimePicker';
import ConfirmCloseCouncilDialog from 'features/Council/components/ConfirmCloseCouncilDialog';
import ConfirmDialog from 'components/ConfirmDialog';

const formOptions = { resolver: yupResolver(updateCouncilValidationSchema) };
export default function EditForManyProjectProposal() {
  // #region Data
  const { t } = useTranslation();
  const { councilId } = useParams();
  const navigate = useNavigate();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleAddProjectProposal, setIsVisibleAddProjectProposal] = useState(false);
  const [isVisibleConfirmDoneDialog, setIsVisibleConfirmDoneDialog] = useState(false);
  const [isSentMail, setIsSentMail] = useState(false);
  const [isVisibleConfirmDeleteMemberDialog, setIsVisibleConfirmDeleteMemberDialog] =
    useState(false);
  const [dataUpdateCouncil, setDataUpdateCouncil] = useState({});
  const [proposalMembers, setProposalMembers] = useState([]);
  const [isCloseCouncil, setIsCloseCouncil] = useState([false, true]);
  const [isVisibleConfirmDeletePPDialog, setIsVisibleConfirmDeletePPDialog] = useState(false);
  const [ppDelete, setPPDelete] = useState(null);
  const { projectProposalId } = useParams();

  const {
    data: _data,
    isLoading: isDataLoading,
    refetch,
  } = useQuery({
    queryKey: [councilId],
    queryFn: () => getCouncil(councilId),
  });

  const councilData = useMemo(() => _data?.data ?? [], [_data]);

  const { data: councilMemberRolesData } = useQuery(
    ['councilMemberRoles'],
    getAllCouncilMemberRoles,
    {
      staleTime: 5000 * 60,
    }
  );

  const roles = useMemo(() => {
    if (councilMemberRolesData?.data?.length > 0) {
      return councilMemberRolesData.data.map((councilMemberRole) => ({
        value: councilMemberRole.id,
        name: t(`council.role.${councilMemberRole.code}`),
      }));
    }
    return [];
  }, [councilMemberRolesData?.data, t]);

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes, {
    staleTime: 5000 * 60,
  });
  const councilTypes = useMemo(() => councilTypesData?.data ?? [], [councilTypesData]);

  const typeCouncils = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData.data.map((councilType) => ({
        value: councilType.id,
        name: `${t(`council.viewList.${councilType.code}`)}`,
        code: councilType.code,
        id: councilType.id, // use check council type
      }));
    }
    return [];
  }, [councilTypesData?.data, t]);

  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );
  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );
  const allProjectProposalVersions = useMemo(
    () => allProjectProposalVersionsData?.data ?? [],
    [allProjectProposalVersionsData]
  );
  const [previousVersion] = useMemo(() => {
    if (allProjectProposalVersions?.length > 0) {
      const currentCouncilType = councilTypes?.find(
        (item) => item?.id === councilData?.councilType?.id
      );
      return [
        allProjectProposalVersions?.find((item) => item?.version === currentCouncilType?.order),
        allProjectProposalVersions?.reduce(
          (a, b) => (a?.version >= b?.version ? a : b),
          allProjectProposalVersions[0]
        ),
      ];
    }
    return [null, null];
  }, [allProjectProposalVersions, councilTypes]);

  const { data: generalInfoProjectProposalData, isLoading: isLoadingData } = useQuery(
    ['GeneralInfoProjectProposal', previousVersion?.id],
    () => generalInfoForCouncil(previousVersion?.id),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!previousVersion?.id,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectProposalData?.data ?? {},
    [generalInfoProjectProposalData]
  );

  const MemberRef = useRef();
  const ProjectProposalRef = useRef();
  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(editCouncil);
  const formDefaultValues = {
    name: '',
    establishDate: '',
    membersFields: [],
    projectProposalIds: [],
  };
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: formDefaultValues, ...formOptions });

  const {
    fields: membersFields,
    append: appendMembers,
    remove: removeMembers,
    update: updateMembers,
  } = useFieldArray({
    control,
    name: 'councilMembers',
  });

  const {
    fields: projectProposalsFields,
    append: appendProjectProposal,
    remove: removeProjectProposal,
    // update: updateProjectProposal,
  } = useFieldArray({
    control,
    name: 'councilProjectProposals',
    keyName: 'customId',
  });
  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  // const handleSelectedChange = (selectedItems) => {
  //   const projectProposalIds = selectedItems?.map((item) => item?.id);
  //   setValue('projectProposalIds', projectProposalIds);
  // };
  const decisionToEstablishFileUrl = watch('decisionToEstablishFileUrl');
  const getPageMargins = () => '@page { margin: 0.5in 0.5in 0.2in 0.2in  !important; }';

  // #endregion
  // #region Events
  const onSubmit = async (data) => {
    const formData = new FormData();

    // check member
    if (membersFields.length === 0) {
      toast(TOAST.ERROR, t('errorMessage.councilMustHaveAtLeastOneMember'));
      return;
    }

    // check project proposal
    if (projectProposalsFields.length === 0) {
      toast(TOAST.ERROR, t('errorMessage.councilMustHaveAtLeastOneProjectProposal'));
      return;
    }

    if (data?.decisionToEstablish) {
      formData.append('establishmentFile', data.decisionToEstablish);
    }
    if (data?.establishDate) {
      formData.append('timeCreated', getEndingTimeOfDate(new Date(data.establishDate)));
    }
    if (data?.reviewDeadline) {
      formData.append('reviewDeadline', getEndingTimeOfDate(new Date(data.reviewDeadline)));
    }
    if (data?.timeMet) {
      formData.append('timeMet', new Date(data.timeMet));
    }
    if (data?.meetingLocation) {
      formData.append('meetingLocation', data.meetingLocation);
    }
    if (data?.contact) {
      formData.append('contact', data.contact);
    }
    if (data?.documentUrl) {
      formData.append('documentUrl', data.documentUrl);
    }
    formData.append('name', data.name);
    formData.append('members', JSON.stringify(membersFields));
    const projectProposalIds = data?.councilProjectProposals?.map((item) => item?.id);
    formData.append('projectProposalIds', projectProposalIds);
    formData.append('councilId', councilId);

    mutate(formData, {
      onSuccess: (res) => {
        const currentValues = watch();
        refetch();
        setIsSentMail(false);
        toast(TOAST.SUCCESS, t('council.edit.success'));
        if (data.decisionToEstablish && res?.data?.establishmentFileUrl) {
          currentValues.decisionToEstablishFileUrl = res?.data?.establishmentFileUrl;
        }
        reset({
          ...currentValues,
          decisionToEstablish: null,
        });
      },
      onError: (res) => {
        if (res?.response?.data?.message === 'Error delete old members') {
          setDataUpdateCouncil(formData);
          setIsVisibleConfirmDeleteMemberDialog(true);
        } else {
          setIsVisibleDialogError(true);
        }
      },
    });
  };

  useEffect(() => {
    if (councilData?.id) {
      setValue('name', councilData?.name);
      setValue('councilType', councilData?.councilType?.id);
      setValue('establishDate', new Date(councilData?.timeCreated));
      setValue('reviewDeadline', new Date(councilData?.projectProposalCouncil?.reviewDeadline));

      if (councilData?.timeMet) {
        setValue('timeMet', new Date(councilData?.timeMet));
      }
      setValue('meetingLocation', councilData?.meetingLocation);
      setValue('contact', councilData?.contact);
      setValue('documentUrl', councilData?.documentUrl);
      setValue(
        'councilMembers',
        councilData?.councilMembers?.map((member) => ({
          academicDegree: member?.academicDegree,
          academicRank: member?.academicRank,
          contact: member?.contact,
          email: member?.contact?.email,
          fullname: member?.contact?.fullname,
          id: member?.id,
          organizationId: member?.contact?.organizationId,
          roleId: member?.roleId,
          specificOrganizationName: member?.contact?.specificOrganizationName,
        }))
      );

      setValue(
        'councilProjectProposals',
        councilData?.projectProposals?.map((projectProposal) => ({
          vietnameseName: projectProposal?.vietnameseName,
          projectType: projectProposal?.projectType,
          fullName: projectProposal?.ownerContact?.fullname,
          program: projectProposal?.program?.name,
          programId: projectProposal?.program?.id,
          researchFields: projectProposal?.projectProposalResearchFields,
          id: projectProposal?.id,
          reference: projectProposal?.reference,
          organizationId: projectProposal?.contact?.organizationId,
          projectProposalContacts: projectProposal?.projectProposalContacts,
          hasCouncilResult: projectProposal?.hasCouncilResult,
          hasReview: projectProposal?.hasReview,
        }))
      );

      setValue(
        'decisionToEstablishFileUrl',
        councilData?.projectProposalCouncil?.establishmentFileUrl
      );
    }
  }, [JSON.stringify(councilData)]);

  useEffect(() => {
    if (projectProposalsFields.length > 0) {
      const membersOfProposal = [];
      projectProposalsFields.forEach((pp) => {
        membersOfProposal.push(...pp.projectProposalContacts);
      });
      setProposalMembers([...membersOfProposal]);
    }
  }, [projectProposalsFields]);

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const handleDownloadFile = (path, id) => {
    mutateDownloadFile(
      { path, id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const handleCloseCouncil = () => {
    if (councilData && !councilData?.completedAt) {
      setIsCloseCouncil([true, false]);
    } else {
      navigate(-1);
    }
  };
  // #endregion

  return (
    <>
      {(isLoadingData || isLoadingCreate || isDataLoading) && <Loading />}
      {isErrorRes && (
        <ErrorDialog
          title={t('council.edit.titleEditCouncilError')}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ConfirmSendMailMultipleProjectDialog
        visible={isVisibleConfirmDoneDialog}
        setVisible={setIsVisibleConfirmDoneDialog}
        setIsSentMail={setIsSentMail}
        council={councilData}
        refetch={refetch}
      />
      <ReactRouterPrompt
        when={
          (isDirty && Object.keys(dirtyFields).length) ||
          (councilData && !councilData?.completedAt && isCloseCouncil[1])
        }
      >
        {({ isActive, onConfirm, onCancel }) => (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {councilData && !councilData?.completedAt ? (
              <ConfirmCloseCouncilDialog
                visible={isActive}
                setVisible={setIsCloseCouncil}
                onConfirm={onConfirm}
                onHide={onCancel}
              />
            ) : (
              <Dialog
                visible={isActive}
                header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                position="center"
                style={{ width: '300px' }}
                onHide={onCancel}
                draggable={false}
                footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
              >
                <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
              </Dialog>
            )}
          </>
        )}
      </ReactRouterPrompt>
      <div className="card relative create-council-for-many-projectproposal">
        <style>{getPageMargins()}</style>
        <div className="flex">
          <h3 className="mb-3 line-height-3 text-4xl text-center font-bold flex-1">
            {t('council.viewList.update')} {t(`council.viewList.${councilData?.councilType?.code}`)}
          </h3>
        </div>

        <form className="form-create-council ml-5" onSubmit={handleSubmit(onSubmit)}>
          <h4 className="mt-4">{t('council.createForManyProjectProposal.infoCouncil.label')}</h4>
          <div className="relative p-fluid">
            <div>
              <TextInput
                label={t('council.createForManyProjectProposal.infoCouncil.name')}
                isRequired
                name="name"
                isFontWeightLabel
                control={control}
                errors={errors}
              />
            </div>
            <div className="flex flex-wrap gap-6 ">
              <div className="w-3">
                <Select
                  label={t('council.createForManyProjectProposal.infoCouncil.councilType')}
                  name="councilType"
                  isFontWeightLabel
                  isRequired
                  disabled
                  control={control}
                  errors={errors}
                  options={typeCouncils}
                />
              </div>
              <div className="w-3">
                <DatePicker
                  label={t('council.fields.establishDate')}
                  isRequired
                  name="establishDate"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
              <div className="w-3">
                <DatePicker
                  label={t('council.fields.reviewDeadline')}
                  name="reviewDeadline"
                  isRequired
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-3">
                <DateTimePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.timeMet')}
                  name="timeMet"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.meetingLocation')}
                  name="meetingLocation"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.contact')}
                  name="contact"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.documentUrl')}
                  name="documentUrl"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                />
              </div>
            </div>

            <div>
              <FileInput
                label={t('council.fields.decisionToEstablish')}
                isFontWeightLabel
                name="decisionToEstablish"
                accept=".pdf"
                acceptOnly="pdfOnly"
                control={control}
                errors={errors}
              />
              {decisionToEstablishFileUrl && (
                <span
                  className="p-button p-button-text p-button-sm mb-3"
                  onClick={() => handleDownloadFile(decisionToEstablishFileUrl, councilId)}
                >
                  <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                  <span className="text-blue-500 underline font-italic text-base">
                    {formatFileName(decisionToEstablishFileUrl)}
                  </span>
                </span>
              )}
            </div>
          </div>

          <h4 className="mt-5 mb-3">
            {t('council.createForManyProjectProposal.listMemberCouncil.label')}
          </h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                    {`${t('council.member.fields.academicRank')}, ${t(
                      'council.member.fields.academicDegree'
                    )}, ${t('council.member.fields.name')}`}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    Email
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.organization')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.role')}
                  </th>

                  <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                    {t('council.member.fields.action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  <td colSpan={6}>
                    <div>
                      <span>{t('council.create.add')}</span>
                      <i
                        onClick={() => {
                          MemberRef.current.open({
                            append: appendMembers,
                            type: 'APPEND',
                          });
                        }}
                        className="add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
                        data-pr-tooltip={t('formLayout.action.add')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".add-scientific-secretary" />
                    </div>
                  </td>
                </tr>
                {membersFields.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      {`${getAcadamicLevelShortHand(
                        item?.academicRank,
                        item?.academicDegree,
                        t,
                        true
                      )}${item?.fullname}`}
                    </td>
                    <td>{item?.email}</td>
                    <td>
                      {typeof value === 'object'
                        ? getContactOrganizationName(organizations, item?.contact, t)
                        : getOrganizationNameById(
                            item?.organizationId,
                            organizations,
                            item?.specificOrganizationName,
                            t
                          )}
                    </td>
                    <td>{getNameRoleOfMemberInCouncil(roles, item?.roleId)}</td>
                    <td className="flex flex-column float text-center p-0 border-none border-bottom-1">
                      <i
                        onClick={() => {
                          MemberRef.current.open({
                            update: updateMembers,
                            index,
                            value: item,
                            type: 'UPDATE',
                          });
                        }}
                        className="edit-icon pi pi-pencil bg-red text-primary cursor-pointer my-2"
                        data-pr-tooltip={t('formLayout.action.edit')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".edit-icon" />

                      <i
                        onClick={() => removeMembers(index)}
                        className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                        data-pr-tooltip={t('formLayout.action.remove')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".remove-icon" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="mt-5 mb-3">
            C. {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.label')}
          </h4>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 260, width: '40%' }}>
                    {t(
                      'council.createForManyProjectProposal.listProjectProposalOfCouncil.vietnameseName'
                    )}
                  </th>
                  <th className="text-center" style={{ minWidth: 140, width: '15%' }}>
                    {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.owner')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '20%' }}>
                    {t(
                      'council.createForManyProjectProposal.listProjectProposalOfCouncil.researchField'
                    )}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '20%' }}>
                    {t('council.createForManyProjectProposal.listProjectProposalOfCouncil.program')}
                  </th>
                  <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                    {t('council.member.fields.action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  <td colSpan={6}>
                    <div>
                      <span>
                        {projectProposalsFields.length === 0
                          ? t(
                              'council.createForManyProjectProposal.listProjectProposalOfCouncil.add'
                            )
                          : t(
                              'council.createForManyProjectProposal.listProjectProposalOfCouncil.update'
                            )}
                      </span>
                      <i
                        onClick={() => {
                          ProjectProposalRef.current.open({
                            append: appendProjectProposal,
                            fields: projectProposalsFields,
                            type: projectProposalsFields.length === 0 ? 'APPEND' : 'UPDATE',
                            remove: removeProjectProposal,
                          });
                        }}
                        className={
                          projectProposalsFields.length === 0
                            ? 'add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1'
                            : 'add-scientific-secretary pi pi-pencil text-primary bg-primary cursor-pointer p-1 border-circle ml-1'
                        }
                        data-pr-tooltip={
                          projectProposalsFields.length === 0
                            ? t('formLayout.action.add')
                            : t('formLayout.action.update')
                        }
                        data-pr-position="left"
                      />
                      <Tooltip target=".add-scientific-secretary" />
                    </div>
                  </td>
                </tr>
                {projectProposalsFields.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={item?.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>{item?.vietnameseName}</td>
                    <td>{item?.fullName}</td>
                    <td>
                      {item?.researchFields
                        ?.sort((a, b) => {
                          if (a.priority === null) return 1;
                          if (b.priority === null) return -1;
                          if (a.priority === b) return 0;
                          return a.priority < b.priority ? -1 : 1;
                        })
                        ?.map((researchField, index2) => (
                          <p key={researchField.researchField.id} className="text-left m-0">
                            {`(${researchField?.priority ?? index2 + 1}) ${t(
                              `researchFields.${researchField.researchField.code}`
                            )}`}
                          </p>
                        ))}
                    </td>
                    <td>{item?.program}</td>
                    <td className="float text-center p-0 border-none border-bottom-1 align-center">
                      <i
                        onClick={() => {
                          if (item?.hasCouncilResult) {
                            toast(TOAST.ERROR, t('errorMessage.projectProposalHasResult'));
                            return;
                          }
                          if (item?.hasReview) {
                            setIsVisibleConfirmDeletePPDialog(true);
                            setPPDelete({ vietnameseName: item?.vietnameseName, index });
                            return;
                          }
                          if (!item?.hasReview && !item.hasCouncilResult) {
                            removeProjectProposal(index);
                          }
                        }}
                        className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                        data-pr-tooltip={t('formLayout.action.remove')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".remove-icon" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-content-end mt-8">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              onClick={handleCloseCouncil}
              className="mr-2 w-8rem"
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              onClick={checkValidation}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
              className="mr-2 w-8rem"
            />
            <Can do="send_mail" on="council">
              <Button
                label={
                  councilData?.completedAt
                    ? councilData?.isSentMailAllMembers
                      ? t('formLayout.action.doneAndSendMail')
                      : t('formLayout.action.sentEmailToMembers')
                    : t('formLayout.action.doneAndSendMail')
                }
                severity="success"
                type="button"
                onClick={() => setIsVisibleConfirmDoneDialog(true)}
                disabled={
                  (isDirty && Object.keys(dirtyFields).length) ||
                  isSentMail ||
                  councilData?.completedAt
                    ? councilData?.isSentMailAllMembers
                    : councilData?.completedAt
                }
                className="mr-2 w-auto"
              />
            </Can>
          </div>
          <MemberDialog
            ref={MemberRef}
            organizations={organizations}
            curMembers={membersFields}
            roles={roles}
            projectProposalMembers={proposalMembers}
            councilTypeSelected={councilData?.councilType?.code}
          />
          <ConfirmDeleteMemberMulDialog
            visible={isVisibleConfirmDeleteMemberDialog}
            setVisible={setIsVisibleConfirmDeleteMemberDialog}
            data={dataUpdateCouncil}
            reference={projectProposalId}
            reset={reset}
            watch={watch}
            setIsSentMail={setIsSentMail}
            refetch={refetch}
          />
          <AddProjectProposalDialog
            ref={ProjectProposalRef}
            curProjectProposals={projectProposalsFields}
            projectProposalMembers={generalInfoProjectProposal?.projectProposalContacts}
            curMembers={membersFields}
            councilTypeSelected={councilData?.councilType}
            isVisible={isVisibleAddProjectProposal}
            setIsVisible={setIsVisibleAddProjectProposal}
          />

          <ConfirmDialog
            visible={isVisibleConfirmDeletePPDialog}
            className="p-text-center w-3"
            onHide={() => setIsVisibleConfirmDeletePPDialog(false)}
            content={
              <div className="mr-6 ml-1">
                <span>
                  {' '}
                  {t(
                    'council.createForManyProjectProposal.listProjectProposalOfCouncil.confirmDelete.project'
                  )}{' '}
                </span>
                <b>{ppDelete?.vietnameseName}</b>
                <span>
                  {' '}
                  {t(
                    'council.createForManyProjectProposal.listProjectProposalOfCouncil.confirmDelete.message'
                  )}{' '}
                </span>
              </div>
            }
            icon="pi pi-exclamation-triangle text-red-500"
            onReject={() => setIsVisibleConfirmDeletePPDialog(false)}
            onAccept={() => {
              setIsVisibleConfirmDeletePPDialog(false);
              removeProjectProposal(ppDelete?.index);
            }}
          />
          <ConfirmCloseCouncilDialog visible={isCloseCouncil[0]} setVisible={setIsCloseCouncil} />
        </form>
      </div>
    </>
  );
}
